import { fabric } from 'fabric';
import DOMPurify from 'dompurify';

type ImagePlaceholderLike = fabric.Group & {
    hasMask: boolean;
    item: (index: number) => fabric.Object;
    _updateSvgClipPath: () => void;
    getWidth: () => number;
    getHeight: () => number;
};

type clipPathObjectType = {
    path?: unknown[];
    type?: string;
    canvas?: unknown;
    ownMatrixCache?: unknown;
    cacheHeight?: unknown;
    cacheTranslationX?: unknown;
    cacheTranslationY?: unknown;
    editedProps?: unknown;
    excludeFromUpdateLock?: unknown;
    ownCaching?: unknown;
    cacheWidth?: unknown;
    _cacheContext?: unknown;
    _cacheProperties?: unknown;
    _transformDone?: unknown;
    _cacheCanvas?: unknown;
};

const resetUnusedProperties = (object: clipPathObjectType) => {
    // Properties that are not needed for the clipPath object
    const propertiesToReset: (keyof clipPathObjectType)[] = [
        'canvas',
        'ownMatrixCache',
        'cacheHeight',
        'cacheTranslationX',
        'cacheTranslationY',
        'editedProps',
        'excludeFromUpdateLock',
        'ownCaching',
        'cacheWidth',
        '_cacheContext',
        '_cacheProperties',
        '_transformDone',
        '_cacheCanvas'
    ];

    propertiesToReset.forEach((property) => {
        object[property] = undefined;
    });
};

export const loadClipPathFromObject = async (object: clipPathObjectType | fabric.Rect | fabric.Path) => {
    // If the object is already an instance of fabric.Rect or fabric.Path, return accordingly
    if (object instanceof fabric.Rect) {
        return object;
    }
    if (object instanceof fabric.Path) {
        return object.path && object.path.length > 0 ? object : undefined;
    }

    // If the object is a plain object, validate the path property and create a fabric.Path instance if valid
    if (object.path && object.path.length > 0) {
        resetUnusedProperties(object);
        return new Promise((resolve) => {
            fabric.Path.fromObject(object, (clipPath: fabric.Path) => {
                clipPath.set({
                    originX: 'center',
                    originY: 'center'
                });
                resolve(clipPath);
            });
        });
    }

    if (object.type === 'rect') {
        return fabric.Rect.fromObject(object);
    }

    return undefined;
};

export const sanitizeSvgString = (svgString: string): string => {
    return DOMPurify.sanitize(svgString, { USE_PROFILES: { svg: true, svgFilters: true } });
};

const SVG_PARSE_TIMEOUT_MS = 5000;

export const parseSvgMaskShape = (sanitizedSvgString: string): Promise<fabric.Group> => {
    return new Promise((resolve, reject) => {
        let settled = false;

        const timeout = setTimeout(() => {
            if (settled) return;
            settled = true;
            reject(new Error('Timed out parsing SVG mask - no renderable content found.'));
        }, SVG_PARSE_TIMEOUT_MS);

        fabric.loadSVGFromString(sanitizedSvgString, (results, svgOptions) => {
            if (settled) return;
            settled = true;
            clearTimeout(timeout);

            if (!results || results.length === 0) {
                reject(new Error('SVG mask has no renderable content.'));
                return;
            }

            const canvasWidth = svgOptions?.width;
            const canvasHeight = svgOptions?.height;
            const shape =
                canvasWidth! > 0 && canvasHeight! > 0
                    ? new fabric.Group(results, {
                          width: canvasWidth,
                          height: canvasHeight,
                          centerPoint: { x: canvasWidth! / 2, y: canvasHeight! / 2 }
                      } as fabric.IGroupOptions)
                    : (fabric.util.groupSVGElements(results, svgOptions) as fabric.Group);
            if (!(shape.width! > 0) || !(shape.height! > 0)) {
                reject(new Error('SVG mask resolved to a zero-size bounding box.'));
                return;
            }

            resolve(shape);
        });
    });
};

const cloneGroup = (group: fabric.Group): Promise<fabric.Group> => {
    return new Promise((resolve) => {
        group.clone((clone: fabric.Group) => resolve(clone));
    });
};

export const applySvgMaskClipPath = async (
    imagePlaceholder: ImagePlaceholderLike,
    svgMaskShape: fabric.Group
): Promise<void> => {
    const clipPath = await cloneGroup(svgMaskShape);
    const center = imagePlaceholder.getCenterPoint();
    clipPath.absolutePositioned = true;
    clipPath.left = center.x;
    clipPath.top = center.y;
    clipPath.originX = 'center';
    clipPath.originY = 'center';
    clipPath.angle = imagePlaceholder.angle || 0;
    clipPath.scaleX = imagePlaceholder.getWidth() / clipPath.width!;
    clipPath.scaleY = imagePlaceholder.getHeight() / clipPath.height!;

    imagePlaceholder.item(0).set('clipPath', clipPath);
    imagePlaceholder.hasMask = true;
    imagePlaceholder._updateSvgClipPath();
};
